import React, { useState, useRef, useCallback, useEffect, memo } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  TextInput,
  Image,
  Platform,
  ScrollView,
} from "react-native";
import { Picker } from "@react-native-picker/picker";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useTranslation } from "./i18n/useTranslation";
import SegmentsRendererRN from "./SegmentsRendererRN";
import quizStyles from "./quizStyles";
import ExamDrawingAnswer from "./components/ExamDrawingAnswer"; // BDD 08/30/2026 - official exam drawing response
import html2canvas from "html2canvas"; // BDD 08/30/2026 - capture reviewed test question for Ask AI


// BDD 08/30/2026 - first rendered question image can optionally become
// the background of an official-exam drawing workspace.
const getFirstRichImageUri = (payload) => {
  if (!payload || typeof payload !== "object") return null;

  if (Array.isArray(payload.segments)) {
    for (const segment of payload.segments) {
      if (String(segment?.type || "").toLowerCase() !== "image") continue;
      const uri = segment?.src || segment?.base64 || segment?.uri || segment?.source_image_url;
      if (typeof uri === "string" && uri.trim()) return uri.trim();
    }
  }

  if (Array.isArray(payload.images)) {
    for (const image of payload.images) {
      const uri = image?.base64 || image?.src || image?.uri || image?.source_image_url;
      if (typeof uri === "string" && uri.trim()) return uri.trim();
    }
  }

  return null;
};


/**
 * QuestionDisplay
 *
 * Renders a *list* of questions (the whole quiz/test).
 *
 * Props:
 * - mode: "testTaking" | "pastTestView" | "quizPractice" | "previewStatic"
 * - quiz: array of question objects
 * - isDarkMode: bool
 * - styles: your styles() function (the same one you call everywhere else)
 *
 * The rest are the same handlers/state you were already passing to renderQuizSection:
 * - userAnswers
 * - answerFeedback
 * - showHints
 * - showSolutions
 * - toggleHint
 * - toggleSolution
 * - handleAnswerChange
 * - handleMCQSelection
 * - checkTextAnswer
 *
 * NOTE:
 * "previewStatic" is what TeacherTestPreview uses. It renders read-only:
 *   - no touchables that mutate answers
 *   - no hint/solution buttons
 *   - no TextInputs
 */

// Window in which repeat events from one tap/click count as the same gesture.
const MULTI_SELECT_DEBOUNCE_MS = 300;

export default function QuestionDisplay({
  mode = "quizPractice",
  quiz,
  isDarkMode,
  userAnswers = {},
  answerFeedback = {},
  showHints = {},
  showSolutions = {},
  toggleHint = () => {},
  toggleSolution = () => {},
  handleAnswerChange = () => {},
  handleMCQSelection = () => {},
  handleMultiSelect = () => {},
  checkTextAnswer = () => {},
  onNavStateChange = null, // callback(navState) for parent to render nav outside scroll
  // AIT-249 props -----------------------------------------------------------
  // immediateFeedback: when true (legacy), MCQ/TF rows color green/red the
  //   instant a student picks an option. When false (default), the row only
  //   gets a neutral "selected" highlight until the student submits.
  // isReviewMode: render-time hint that we're in a post-submit review (either
  //   "quizReview" for practice or "pastTestView" for tests). When true, the
  //   per-question Correct/Incorrect/Skipped state + correct-answer reveal
  //   are shown regardless of `immediateFeedback`.
  // reviewSummary: optional aggregate { total, correct, wrong, unattempted,
  //   score, totalScore, perQuestion } from AITutorApp.buildReviewSummary;
  //   used to render the summary header card.
  immediateFeedback = false,
  isReviewMode = false,
  reviewSummary = null,
  onAskAI = null, // BDD 08/30/2026 - post-test AI help
}) {
  const { t } = useTranslation();
  const S = quizStyles(isDarkMode);

  // BDD 08/30/2026 - DOM refs used to capture one rendered question card for Ask AI
  const questionCardRefs = useRef({});

  // AIT-297: last fire time per "questionId:optionKey" for multi-select toggles
  const lastMultiSelectFireRef = useRef({});

  // BDD 08/30/2026 - capture exactly one rendered review question for Ask AI
  const captureQuestionCardForAI = useCallback(async (question, index) => {
    if (Platform.OS !== "web") {
      console.warn("Ask AI question capture is currently enabled for web only.");
      return null;
    }

    const questionKey = question?.question_id ?? index;
    const node = questionCardRefs.current[questionKey];

    if (!node) {
      throw new Error(`Question card DOM node not found for ${questionKey}`);
    }

    const canvas = await html2canvas(node, {
      backgroundColor: "#ffffff",
      useCORS: true,
      logging: false,
      scale: Math.min(window.devicePixelRatio || 1, 2),
      // BDD 08/30/2026 - do not include Ask AI controls in the image sent to AI
      ignoreElements: (element) =>
        element?.getAttribute?.("data-html2canvas-ignore") === "true",
    });

    return canvas.toDataURL("image/png");
  }, []);

  const handleAskAIForReviewedQuestion = useCallback(
    async (question, index) => {
      if (typeof onAskAI !== "function") return;

      try {
        const screenshot = await captureQuestionCardForAI(question, index);

        await onAskAI({
          question,
          questionIndex: index,
          screenshot,
        });
      } catch (error) {
        console.error("Ask AI question capture failed:", error);
      }
    },
    [onAskAI, captureQuestionCardForAI]
  );

  const isTestMode = mode === "testTaking";
  const isPastTestView = mode === "pastTestView";
  const isPreviewStatic = mode === "previewStatic";
  const isPractice = mode === "quizPractice";

  // AIT-249: unified flag — should we color selected options + reveal correct
  // answers? Always true in any review mode; in live practice, only when
  // the teacher (or legacy default) explicitly opts in via `immediateFeedback`.
  const shouldRevealCorrectness =
    isReviewMode || isPastTestView || (isPractice && immediateFeedback);

  // ------------------------------------------------------------------
  // Small child components (memoized) that render MCQ / TrueFalse rows
  // ------------------------------------------------------------------

  const MCQOption = memo(function MCQOption({
    question,
    optKey,
    idx,
    option,
    isSelected,
    isCorrect,
  }) {
    // In previewStatic we render a simple row, no tapping, no highlight logic.
    if (isPreviewStatic) {
      return (
        <View
          style={[
            S.quizOption,
            {
              borderColor: "#d1d5db",
              borderWidth: 1,
              backgroundColor: isDarkMode ? "#1f2937" : "#fff",
            },
          ]}
        >
          <Text style={S.optionText}>
            {t("Option")} {idx + 1}:
          </Text>

          {Array.isArray(option?.segments) ? (
            <SegmentsRendererRN
              segments={option.segments}
              isDarkMode={isDarkMode}
              baseFontSize={16}
            />
          ) : (
            <>
              {!!option?.text && (
                <Text style={S.optionText}>{option.text}</Text>
              )}
              {!!option?.images?.length &&
                option.images.map((img, imgIdx) => (
                  <Image
                    key={imgIdx}
                    source={{ uri: img.base64 }}
                    style={{
                      width:
                        img.width && img.width < 150
                          ? img.width
                          : img.orientation === "horizontal"
                          ? "100%"
                          : "50%",
                      height:
                        img.height && img.height < 150
                          ? img.height
                          : img.orientation === "vertical"
                          ? 300
                          : 250,
                      resizeMode: "contain",
                      marginVertical: 5,
                    }}
                  />
                ))}
            </>
          )}
        </View>
      );
    }

    // Interactive version (student quiz / pastTestView)
    const firedRef = useRef(false);
    const fire = useCallback(() => {
      if (firedRef.current) return;
      firedRef.current = true;
      setTimeout(() => {
        firedRef.current = false;
      }, 200);

      // signature from your code:
      // handleMCQSelection(qid, optKey, idx, correctAnswerString)
      handleMCQSelection(
        question.question_id,
        optKey,
        idx,
        String(question?.options?.correct_answer ?? "")
      );
    }, [question, optKey, idx]);

    const baseStyle = S.quizOption;

    // AIT-249 selection highlight rules:
    // - Live testTaking (always): neutral blue border.
    // - Practice / pastTestView with shouldRevealCorrectness === false
    //   (deferred mode, default): same neutral blue border — DO NOT color
    //   green/red while answering.
    // - With shouldRevealCorrectness === true (review or legacy immediate
    //   feedback): color green if correct, red if wrong.
    const selectedStyle = !shouldRevealCorrectness
      ? { borderColor: "#007BFF", borderWidth: 2 }
      : isSelected
        ? isCorrect
          ? S.correctAnswer
          : S.wrongAnswer
        : {};

    return (
      <TouchableOpacity
        activeOpacity={0.85}
        onPress={fire}
        {...(Platform.OS === "web"
          ? {
              onClick: (e) => {
                e.preventDefault?.();
                fire();
              },
              onMouseUp: () => fire(),
              onTouchEnd: () => fire(),
              onKeyDown: (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault?.();
                  fire();
                }
              },
            }
          : {})}
        style={[
          baseStyle,
          isSelected && selectedStyle,
          Platform.OS === "web"
            ? {
                userSelect: "none",
                cursor: "pointer",
                touchAction: "manipulation",
              }
            : null,
        ]}
      >
        <Text style={S.optionText}>
          {t("Option")} {idx + 1}:
        </Text>

        {Array.isArray(option?.segments) ? (
          <SegmentsRendererRN
            segments={option.segments}
            isDarkMode={isDarkMode}
            baseFontSize={16}
          />
        ) : (
          <>
            {!!option?.text && (
              <Text style={S.optionText}>{option.text}</Text>
            )}
            {!!option?.images?.length &&
              option.images.map((img, imgIdx) => (
                <Image
                  key={imgIdx}
                  source={{ uri: img.base64 }}
                  style={{
                    width:
                      img.width && img.width < 150
                        ? img.width
                        : img.orientation === "horizontal"
                        ? "100%"
                        : "50%",
                    height:
                      img.height && img.height < 150
                        ? img.height
                        : img.orientation === "vertical"
                        ? 300
                        : 250,
                    resizeMode: "contain",
                    marginVertical: 5,
                  }}
                />
              ))}
          </>
        )}
      </TouchableOpacity>
    );
  });

  // ------------------------------------------------------------------
  // Multi-select option (question_type 4). Multiple options can be chosen;
  // tapping toggles membership via handleMultiSelect. Review coloring marks
  // every correct option green, and any wrongly-selected option red.
  // ------------------------------------------------------------------
  const MSOption = memo(function MSOption({
    question,
    optKey,
    idx,
    option,
    isSelected,
  }) {
    if (isPreviewStatic) {
      return (
        <View
          style={[
            S.quizOption,
            {
              borderColor: "#d1d5db",
              borderWidth: 1,
              backgroundColor: isDarkMode ? "#1f2937" : "#fff",
            },
          ]}
        >
          <Text style={S.optionText}>
            {t("Option")} {idx + 1}:
          </Text>
          {Array.isArray(option?.segments) ? (
            <SegmentsRendererRN
              segments={option.segments}
              isDarkMode={isDarkMode}
              baseFontSize={16}
            />
          ) : (
            !!option?.text && <Text style={S.optionText}>{option.text}</Text>
          )}
        </View>
      );
    }

    // AIT-297: a toggle is NOT idempotent, and one web click delivers
    // mouseup + click + press. MSOption remounts on every parent render
    // (it is defined inside render), so a per-instance guard resets after the
    // first toggle and the second event un-ticks the box. Key the guard on
    // the long-lived QuestionDisplay instead.
    const fire = useCallback(() => {
      const guardKey = `${question.question_id}:${optKey}`;
      const now = Date.now();
      if (now - (lastMultiSelectFireRef.current[guardKey] ?? -Infinity) < MULTI_SELECT_DEBOUNCE_MS) {
        return;
      }
      lastMultiSelectFireRef.current[guardKey] = now;
      handleMultiSelect(question.question_id, optKey);
    }, [question, optKey]);

    // correct_answer is a pipe/comma delimited list of 1-based indices.
    const correctSet = new Set(
      String(question?.options?.correct_answer ?? "")
        .split(/[|,]/)
        .map((s) => parseInt(s, 10))
        .filter((n) => !isNaN(n))
    );
    const isOptionCorrect = correctSet.has(idx + 1);

    const rowStyle = !shouldRevealCorrectness
      ? isSelected
        ? { borderColor: "#007BFF", borderWidth: 2 }
        : {}
      : isOptionCorrect
        ? S.correctAnswer
        : isSelected
          ? S.wrongAnswer
          : {};

    return (
      <TouchableOpacity
        activeOpacity={0.85}
        onPress={fire}
        {...(Platform.OS === "web"
          ? {
              onClick: (e) => {
                e.preventDefault?.();
                fire();
              },
              onMouseUp: () => fire(),
              onTouchEnd: () => fire(),
              onKeyDown: (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault?.();
                  fire();
                }
              },
            }
          : {})}
        style={[
          S.quizOption,
          rowStyle,
          Platform.OS === "web"
            ? {
                userSelect: "none",
                cursor: "pointer",
                touchAction: "manipulation",
              }
            : null,
        ]}
      >
        <View
          style={{ flexDirection: "row", alignItems: "center" }}
          pointerEvents="none"
        >
          <MaterialCommunityIcons
            name={isSelected ? "checkbox-marked" : "checkbox-blank-outline"}
            size={20}
            color={isSelected ? "#007BFF" : "#9ca3af"}
            style={{ marginRight: 8 }}
          />
          <Text style={S.optionText}>
            {t("Option")} {idx + 1}:
          </Text>
        </View>
        {Array.isArray(option?.segments) ? (
          <SegmentsRendererRN
            segments={option.segments}
            isDarkMode={isDarkMode}
            baseFontSize={16}
          />
        ) : (
          <>
            {!!option?.text && <Text style={S.optionText}>{option.text}</Text>}
            {!!option?.images?.length &&
              option.images.map((img, imgIdx) => (
                <Image
                  key={imgIdx}
                  source={{ uri: img.base64 }}
                  style={{
                    width:
                      img.width && img.width < 150
                        ? img.width
                        : img.orientation === "horizontal"
                        ? "100%"
                        : "50%",
                    height:
                      img.height && img.height < 150
                        ? img.height
                        : img.orientation === "vertical"
                        ? 300
                        : 250,
                    resizeMode: "contain",
                    marginVertical: 5,
                  }}
                />
              ))}
          </>
        )}
      </TouchableOpacity>
    );
  });

  // ------------------------------------------------------------------
  // Pagination state (only used for testTaking / quizPractice)
  // ------------------------------------------------------------------
  const isPaginated = isTestMode || isPractice;
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  // Expose nav state to parent so it can render nav outside the ScrollView
  useEffect(() => {
    if (onNavStateChange && isPaginated && quiz?.length > 1) {
      onNavStateChange({
        currentIndex: currentQuestionIndex,
        total: quiz.length,
        onPrev: () => setCurrentQuestionIndex((p) => Math.max(0, p - 1)),
        onNext: () => setCurrentQuestionIndex((p) => Math.min(quiz.length - 1, p + 1)),
      });
    }
  }, [currentQuestionIndex, quiz?.length, isPaginated, onNavStateChange]);

  // ------------------------------------------------------------------
  // Render ALL questions
  // ------------------------------------------------------------------

  if (!quiz || !Array.isArray(quiz) || quiz.length === 0) {
    return <Text>{t("No questions available for this topic.")}</Text>;
  }

  // Determine which questions to render
  const questionsToRender = isPaginated
    ? [{ question: quiz[currentQuestionIndex], index: currentQuestionIndex }]
    : quiz.map((q, i) => ({ question: q, index: i }));

  // Helper: check if a question has been answered
  const isAnswered = (q) => {
    if (!q) return false;
    const ans = userAnswers?.[q.question_id];
    if (ans == null) return false;
    if (typeof ans === "string" && ans.trim() === "") return false;
    if (Array.isArray(ans) && ans.every((v) => !v)) return false;
    if (typeof ans === "object" && ans?.answer_type === "drawing") {
      const drawingStrokes = ans?.drawing_state?.strokes;
      const drawingImage = Array.isArray(ans?.fields)
        ? ans.fields.find(
            (field) =>
              String(field?.field_type || "").toLowerCase() === "image" &&
              typeof field?.field_values === "string" &&
              field.field_values.startsWith("data:image")
          )
        : null;
      return Array.isArray(drawingStrokes) && drawingStrokes.length > 0 && !!drawingImage;
    }
    return true;
  };

  // AIT-249: When in any review mode (quizReview or pastTestView), derive an
  // aggregate summary if the parent didn't pass one. This guarantees both
  // practice review and historical test review get the score header card.

  const derivedSummary = isReviewMode && !reviewSummary
    ? (() => {
        let correct = 0;
        let wrong = 0;
        let unattempted = 0;
        let pending = 0;

        quiz.forEach((q) => {
          const fb = answerFeedback?.[q?.question_id];

          if (fb === "correct") {
            correct += 1;
          } else if (fb === "wrong") {
            wrong += 1;
          } else if (fb === "ungraded") {
            pending += 1;
          } else if (isAnswered(q)) {
            // BDD 08/25/2026 - answered manual-grade question
            pending += 1;
          } else {
            unattempted += 1;
          }
        });

        const totalScore = quiz.reduce((sum, q) => {
          const pts =
            q?.external_question_id && q?.official_points != null
              ? Number(q.official_points)
              : Number(q?.score ?? 1);

          return sum + (Number.isFinite(pts) ? pts : 0);
        }, 0);

        return {
          total: quiz.length,
          correct,
          wrong,
          unattempted,
          pending,
          score: correct,
          totalScore,
        };
      })()
    : reviewSummary;

  return (
    <>
      {/* AIT-249: Review summary header (deferred-feedback / past test) */}
      {isReviewMode && derivedSummary && (
        <View style={S.reviewSummaryCard}>
          <Text style={S.reviewSummaryTitle}>
            {t("Score summary")}
          </Text>
          <Text style={S.reviewSummaryScore}>
            {derivedSummary.pending > 0
              ? t("Pending grading")
              : `${derivedSummary.score} / ${derivedSummary.totalScore}`}

          </Text>
          <View style={S.reviewSummaryChipsRow}>
            <View style={[S.reviewSummaryChip, S.reviewSummaryChipCorrect]}>
              <Text style={S.reviewSummaryChipText}>
                {t("Correct")}: {derivedSummary.correct}
              </Text>
            </View>
            <View style={[S.reviewSummaryChip, S.reviewSummaryChipWrong]}>
              <Text style={S.reviewSummaryChipText}>
                {t("Incorrect")}: {derivedSummary.wrong}
              </Text>
            </View>
            {derivedSummary.pending > 0 && (
              <View style={[S.reviewSummaryChip, S.reviewSummaryChipSkipped]}>
                <Text style={S.reviewSummaryChipText}>
                  {t("Pending grading")}: {derivedSummary.pending}
                </Text>
              </View>
            )}
            <View style={[S.reviewSummaryChip, S.reviewSummaryChipSkipped]}>
              <Text style={S.reviewSummaryChipText}>
                {t("Skipped")}: {derivedSummary.unattempted}
              </Text>
            </View>
          </View>
        </View>
      )}

      {/* ── Question Navigator Grid (paginated modes only) ── */}
      {isPaginated && quiz.length > 1 && (
        <View style={S.navigatorContainer}>
          {quiz.map((q, i) => {
            const answered = isAnswered(q);
            const isCurrent = i === currentQuestionIndex;
            const feedback = q ? answerFeedback?.[q.question_id] : null;
            // AIT-249: green/red only when correctness is revealed (review or
            // legacy immediate feedback). Otherwise stay neutral blue.
            const circleStyle = answered
              ? (shouldRevealCorrectness && feedback === "correct")
                ? S.navigatorCircleCorrect
                : (shouldRevealCorrectness && feedback === "wrong")
                  ? S.navigatorCircleWrong
                  : S.navigatorCircleAnswered
              : null;
            return (
              <TouchableOpacity
                key={q?.question_id ?? i}
                onPress={() => setCurrentQuestionIndex(i)}
                style={[
                  S.navigatorCircle,
                  circleStyle,
                  isCurrent && S.navigatorCircleCurrent,
                ]}
                accessibilityLabel={`${t("Question")} ${i + 1}${answered ? `, ${t("answered")}` : ""}`}
              >
                <Text
                  style={[
                    S.navigatorCircleText,
                    answered && S.navigatorCircleTextAnswered,
                    isCurrent && !answered && S.navigatorCircleTextCurrent,
                  ]}
                >
                  {i + 1}
                </Text>
              </TouchableOpacity>
            );
          })}
        </View>
      )}

      {/* ── Progress indicator (paginated modes only) ── */}
      {isPaginated && (
        <Text style={S.progressText}>
          {t("Question")} {currentQuestionIndex + 1} {t("of")} {quiz.length}
        </Text>
      )}

      {questionsToRender.map(({ question, index }) => {
        // Make sure malformed items don't crash printing
        if (!question) return null;

        const qid = question.question_id;
        const qType = parseInt(question.question_type);
        // BDD 08/25/2026 - Lebanon official exam response prompt
        const shortResponsePlaceholder = question.external_question_id
          ? t("Enter your answer...")
          : t("Write a short response (3–5 sentences)...");

        // for MCQ/hints/etc. we read from these maps:
        const answerForThisQ = userAnswers?.[qid];
        const feedbackForThisQ = answerFeedback?.[qid];
        const drawingQuestionImageUri =
          qType === 12 ? getFirstRichImageUri(question?.question) : null;

        const showHintNow = !!showHints?.[qid];
        const showSolNow = !!showSolutions?.[qid];

        // ---- Question Card ----
        // BDD 08/25/2026 - official exams use exact decimal marks
        const questionScore =
          question.external_question_id &&
          question.official_points != null
            ? Number(question.official_points)
            : question.score;

        // BDD 08/25/2026 - show official subquestion number when available
        const officialQuestionNumber =
          question.external_question_id?.match(/-Q(.+)$/)?.[1];

        const displayQuestionLabel = officialQuestionNumber
          ? `${t("Question")} ${officialQuestionNumber}`
          : `${t("Question")} ${index + 1}`;

        return (

            <View
              key={question?.question_id ?? index}
              ref={(node) => {
                const questionKey = question?.question_id ?? index;

                if (node) {
                  questionCardRefs.current[questionKey] = node;
                } else {
                  delete questionCardRefs.current[questionKey];
                }
              }}
              className="teacher-print-question"
              style={S.quizCardContainer}
            >
            {/* Question header with number and points */}
            <View style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <Text style={{ fontSize: 15, fontWeight: "700", color: isDarkMode ? "#e2e8f0" : "#1e293b" }}>
                {displayQuestionLabel}
              </Text>
              {questionScore != null && questionScore > 0 && (
                <Text style={{ fontSize: 13, fontWeight: "600", color: isDarkMode ? "#818cf8" : "#6366f1", backgroundColor: isDarkMode ? "rgba(129,140,248,0.1)" : "rgba(99,102,241,0.08)", paddingHorizontal: 10, paddingVertical: 3, borderRadius: 12 }}>
                  {questionScore} {questionScore === 1 ? t("point") : t("points")}
                </Text>
              )}
            </View>

            {/* CBQ shared passage (case-based questions): rendered above the
                sub-question stem so it stays visible while answering each
                sub-question. `passage` has the same {segments,text,images}
                shape as a question stem. Absent for standalone questions. */}
            {(Array.isArray(question?.passage?.segments) ||
              question?.passage?.text ||
              question?.passage?.images?.length > 0) && (
              <View
                style={{
                  borderWidth: 1,
                  borderColor: isDarkMode ? "#334155" : "#bfdbfe",
                  backgroundColor: isDarkMode ? "rgba(96,165,250,0.08)" : "#eff6ff",
                  borderRadius: 10,
                  padding: 12,
                  marginBottom: 12,
                }}
              >
                <Text
                  style={{
                    fontSize: 12,
                    fontWeight: "700",
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    color: isDarkMode ? "#93c5fd" : "#2563eb",
                    marginBottom: 6,
                  }}
                >
                  {t("Case Study")}
                </Text>
                {Array.isArray(question?.passage?.segments) ? (
                  <SegmentsRendererRN
                    segments={question.passage.segments}
                    isDarkMode={isDarkMode}
                    baseFontSize={16}
                  />
                ) : (
                  <>
                    {!!question?.passage?.text && (
                      <Text style={S.quizQuestion}>
                        {question.passage.text}
                      </Text>
                    )}
                    {question?.passage?.images?.map((image, imgIndex) => (
                      <Image
                        key={`passage-${imgIndex}`}
                        source={{ uri: image.base64 }}
                        style={{
                          width:
                            image.width && image.width < 150
                              ? image.width
                              : image.orientation === "horizontal"
                              ? "100%"
                              : "50%",
                          height:
                            image.height && image.height < 150
                              ? image.height
                              : image.orientation === "vertical"
                              ? 300
                              : 250,
                          resizeMode: "contain",
                          marginVertical: 5,
                        }}
                      />
                    ))}
                  </>
                )}

              </View>
            )}

            {/* Question stem: segments-aware */}
            {Array.isArray(question?.question?.segments) ? (
              <SegmentsRendererRN
                segments={question.question.segments}
                isDarkMode={isDarkMode}
                baseFontSize={16}
              />
            ) : (
              <>
                <Text style={S.quizQuestion}>
                  {question.question?.text || t("No question available")}
                </Text>

                {question.question?.images?.length > 0 &&
                  question.question.images.map((image, imgIndex) => (
                    <Image
                      key={imgIndex}
                      source={{ uri: image.base64 }}
                      style={{
                        width:
                          image.width && image.width < 150
                            ? image.width
                            : image.orientation === "horizontal"
                            ? "100%"
                            : "50%",
                        height:
                          image.height && image.height < 150
                            ? image.height
                            : image.orientation === "vertical"
                            ? 300
                            : 250,
                        resizeMode: "contain",
                        marginVertical: 5,
                      }}
                    />
                  ))}
              </>
            )}

            {/* ========== Question Type Rendering ========== */}

            {qType === 1 ? (
              // 🔡 Fill in the Blank / short text answer
              <>
                {/* parse string -> options if needed */}
                {typeof question.options === "string" &&
                  (() => {
                    try {
                      question.options = JSON.parse(question.options);
                    } catch (e) {
                      console.warn("⚠️ Could not parse question.options:", e);
                    }
                  })()}

                {question.options?.word_bank?.length > 0 && (
                  <View
                    style={{
                      flexDirection: "row",
                      flexWrap: "wrap",
                      marginBottom: 10,
                    }}
                  >
                    <Text style={{ fontWeight: "bold", marginRight: 5 }}>
                      🧩 Word Bank:
                    </Text>
                    {question.options.word_bank.map((word, idx2) => (
                      <Text
                        key={idx2}
                        style={{
                          backgroundColor: "#eee",
                          paddingHorizontal: 8,
                          paddingVertical: 4,
                          borderRadius: 4,
                          marginRight: 8,
                          marginBottom: 6,
                        }}
                      >
                        {word}
                      </Text>
                    ))}
                  </View>
                )}

                {isPreviewStatic ? (
                  // just show an underline area instead of TextInput
                  <View
                    style={{
                      borderBottomWidth: 1,
                      borderColor: "#9ca3af",
                      minHeight: 24,
                      marginVertical: 8,
                    }}
                  />
                ) : (
                  <TextInput
                    style={S.inputField}
                    placeholder={t("Type your answer...")}
                    value={answerForThisQ || ""}
                    onChangeText={(text) =>
                      handleAnswerChange(qid, text)
                    }
                    onBlur={() => {
                      if (!isTestMode) {
                        checkTextAnswer(
                          qid,
                          question.options?.correct_answer
                        );
                      }
                    }}
                  />
                )}
              </>
            ) : qType === 2 ? (
              // ✅ Multiple Choice
              <View>
                {["option1", "option2", "option3", "option4"].map(
                  (optKey, idx2) => {
                    const option = question.options?.[optKey];
                    if (!option) return null;

                    const isSelected = answerForThisQ === optKey;
                    const isCorrect = feedbackForThisQ === "correct";

                    return (
                      <MCQOption
                        key={`${qid}:${optKey}`}
                        question={question}
                        optKey={optKey}
                        idx={idx2}
                        option={option}
                        isSelected={!!isSelected}
                        isCorrect={!!isCorrect}
                      />
                    );
                  }
                )}
              </View>
            ) : qType === 3 ? (
              // ✅ True / False — stored MCQ-style as option1="True",
              // option2="False". Reuse MCQOption so selection + grading work.
              <View>
                {["option1", "option2"].map((optKey, idx2) => {
                  const option = question.options?.[optKey];
                  if (!option) return null;
                  const isSelected = answerForThisQ === optKey;
                  const isCorrect = feedbackForThisQ === "correct";
                  return (
                    <MCQOption
                      key={`${qid}:${optKey}`}
                      question={question}
                      optKey={optKey}
                      idx={idx2}
                      option={option}
                      isSelected={!!isSelected}
                      isCorrect={!!isCorrect}
                    />
                  );
                })}
              </View>
            ) : qType === 4 ? (
              // ☑️ Multiple-select — multiple correct options; answer is an array
              <View>
                <Text
                  style={{
                    fontSize: 13,
                    fontStyle: "italic",
                    color: isDarkMode ? "#94a3b8" : "#6b7280",
                    marginBottom: 6,
                  }}
                >
                  {t("Select all that apply")}
                </Text>
                {["option1", "option2", "option3", "option4"].map(
                  (optKey, idx2) => {
                    const option = question.options?.[optKey];
                    if (!option) return null;
                    const isSelected =
                      Array.isArray(answerForThisQ) &&
                      answerForThisQ.includes(optKey);
                    return (
                      <MSOption
                        key={`${qid}:${optKey}`}
                        question={question}
                        optKey={optKey}
                        idx={idx2}
                        option={option}
                        isSelected={isSelected}
                      />
                    );
                  }
                )}
              </View>
            ) : qType === 9 ? (
              // 🔗 Word Association / Matching pairs
              <View>
                {question.options?.pairs?.map((pair, pairIdx) => {
                  const selectedRight =
                    (answerForThisQ || [])[pairIdx] ?? "";
                  const allRightOptions =
                    question.options.pairs.map(
                      (p) => p.right?.text
                    ) || [];

                  return (
                    <View key={pairIdx} style={{ marginVertical: 10 }}>
                      <Text style={S.quizQuestion}>
                        {t("Match")}: {pair.left?.text}
                      </Text>

                      {isPreviewStatic ? (
                        // show blank picker area in print mode
                        <View
                          style={{
                            borderWidth: 1,
                            borderColor: "#9ca3af",
                            borderRadius: 4,
                            minHeight: 32,
                            justifyContent: "center",
                            paddingHorizontal: 8,
                            backgroundColor: isDarkMode
                              ? "#333"
                              : "#eee",
                          }}
                        >
                          <Text
                            style={{
                              color: isDarkMode ? "#fff" : "#000",
                              fontSize: 14,
                            }}
                          >
                            {selectedRight || t("Select an option")}
                          </Text>
                        </View>
                      ) : (
                        <Picker
                          selectedValue={selectedRight}
                          onValueChange={(value) => {
                            const existing =
                              answerForThisQ || [];
                            const updated = [...existing];
                            updated[pairIdx] = value;
                            handleAnswerChange(qid, updated);
                          }}
                          style={{
                            backgroundColor: isDarkMode
                              ? "#333"
                              : "#eee",
                            color: isDarkMode ? "#fff" : "#000",
                          }}
                        >
                          <Picker.Item
                            label={t("Select an option")}
                            value=""
                          />
                          {allRightOptions.map(
                            (optText, optIdx) => (
                              <Picker.Item
                                key={optIdx}
                                label={optText}
                                value={optText}
                              />
                            )
                          )}
                        </Picker>
                      )}
                    </View>
                  );
                })}
              </View>
            ) : qType === 12 ? (
              // ✏️ Official exam drawing / construction / graph response
              <ExamDrawingAnswer
                value={answerForThisQ}
                onChange={(drawingAnswer) =>
                  handleAnswerChange(qid, drawingAnswer)
                }
                questionImageUri={drawingQuestionImageUri}
                readOnly={isReviewMode || isPastTestView}
                previewStatic={isPreviewStatic}
                isDarkMode={isDarkMode}
              />
            ) : qType === 10 ? (
              // ✍️ Short Essay
              <View>
                {isPreviewStatic ? (
                  <View
                    style={[
                      S.inputField,
                      {
                        minHeight: 100,
                        textAlignVertical: "top",
                        borderStyle: "dashed",
                        borderColor: "#9ca3af",
                      },
                    ]}
                  >
                    <Text
                      style={{
                        color: isDarkMode ? "#9ca3af" : "#6b7280",
                        fontStyle: "italic",
                      }}
                    >
                      {shortResponsePlaceholder}
                    </Text>
                  </View>
                ) : (
                  <TextInput
                    multiline
                    numberOfLines={5}
                    placeholder={shortResponsePlaceholder}
                    value={answerForThisQ || ""}
                    onChangeText={(text) =>
                      handleAnswerChange(qid, text)
                    }
                    style={[
                      S.inputField,
                      { textAlignVertical: "top", minHeight: 100 },
                    ]}
                  />
                )}
              </View>
            ) : qType === 11 ? (
              // 🧾 Long Essay
              <View>
                {isPreviewStatic ? (
                  <View
                    style={[
                      S.inputField,
                      {
                        minHeight: 180,
                        textAlignVertical: "top",
                        borderStyle: "dashed",
                        borderColor: "#9ca3af",
                      },
                    ]}
                  >
                    <Text
                      style={{
                        color: isDarkMode ? "#9ca3af" : "#6b7280",
                        fontStyle: "italic",
                      }}
                    >
                      {t(
                        "Write a detailed response (150–200 words)..."
                      )}
                    </Text>
                  </View>
                ) : (
                  <TextInput
                    multiline
                    numberOfLines={10}
                    placeholder={t(
                      "Write a detailed response (150–200 words)..."
                    )}
                    value={answerForThisQ || ""}
                    onChangeText={(text) =>
                      handleAnswerChange(qid, text)
                    }
                    style={[
                      S.inputField,
                      { textAlignVertical: "top", minHeight: 180 },
                    ]}
                  />
                )}
              </View>
            ) : null}

            {/* AIT-249: Per-question review block — only in review modes.
                Renders a status badge, the correct answer, and the explanation
                (question.solution). Skipped when no answer or no correct answer
                metadata is available, to stay graceful for ungraded types. */}
            {isReviewMode && (() => {
              const status = feedbackForThisQ || "unattempted";
              const correctRaw = question?.options?.correct_answer;
              const optionsObj = question?.options || {};
              // Pretty-format the correct answer for MCQ: show the option text
              // alongside the label (e.g. "Option 2: Four").
              let correctDisplay = null;
              if (qType === 2 && correctRaw != null) {
                const optKey = String(correctRaw).toLowerCase().startsWith("option")
                  ? String(correctRaw).toLowerCase()
                  : `option${parseInt(correctRaw, 10) || ""}`;
                const opt = optionsObj?.[optKey];
                const optTextPart = Array.isArray(opt?.segments)
                  ? null
                  : (opt?.text || "");
                const num = optKey.replace("option", "");
                correctDisplay = (
                  <Text style={S.correctAnswerCalloutText}>
                    {t("Option")} {num}{optTextPart ? `: ${optTextPart}` : ""}
                  </Text>
                );
              } else if (qType === 3 && correctRaw != null) {
                // True/False — may be numeric ("1"/"2") or "True"/"False"
                let label = String(correctRaw);
                const asNum = parseInt(label, 10);
                if (!isNaN(asNum)) label = asNum === 1 ? "True" : "False";
                correctDisplay = (
                  <Text style={S.correctAnswerCalloutText}>
                    {t(label)}
                  </Text>
                );
              } else if (qType === 4 && correctRaw != null) {
                // Multi-select — correct_answer is a delimited list of indices
                const nums = String(correctRaw)
                  .split(/[|,]/)
                  .map((s) => s.trim())
                  .filter(Boolean);
                correctDisplay = (
                  <Text style={S.correctAnswerCalloutText}>
                    {nums.map((n) => `${t("Option")} ${n}`).join(", ")}
                  </Text>
                );
              } else if (qType === 1 && correctRaw != null) {
                const text = Array.isArray(correctRaw)
                  ? correctRaw.join(" / ")
                  : String(correctRaw);
                correctDisplay = (
                  <Text style={S.correctAnswerCalloutText}>{text}</Text>
                );
              }

              const statusLabel = status === "correct"
                ? t("Correct")
                : status === "wrong"
                  ? t("Incorrect")
                  : status === "unattempted"
                    ? t("Skipped")
                    : null;
              const badgeStyle = status === "correct"
                ? S.reviewBadgeCorrect
                : status === "wrong"
                  ? S.reviewBadgeWrong
                  : S.reviewBadgeSkipped;

              return (
                <View style={S.reviewBlock}>
                  {statusLabel && (
                    <View style={[S.reviewBadge, badgeStyle]}>
                      <Text style={S.reviewBadgeText}>{statusLabel}</Text>
                    </View>
                  )}
                  {correctDisplay && (
                    <View style={S.correctAnswerCallout}>
                      <Text style={S.correctAnswerCalloutLabel}>
                        {t("Correct answer")}:
                      </Text>
                      {correctDisplay}
                    </View>
                  )}
                  {(question?.solution?.text ||
                    Array.isArray(question?.solution?.segments)) && (
                    <View style={S.reviewExplanationBlock}>
                      <Text style={S.reviewExplanationLabel}>
                        {t("Explanation")}
                      </Text>
                      {Array.isArray(question?.solution?.segments) ? (
                        <SegmentsRendererRN
                          segments={question.solution.segments}
                          isDarkMode={isDarkMode}
                          baseFontSize={15}
                        />
                      ) : (
                        <Text style={S.reviewExplanationText}>
                          {question.solution.text}
                        </Text>
                      )}
                    </View>
                  )}
                </View>
              );
            })()}

            {/* 💡 Hints button / content
               Hide entirely for previewStatic and for live testTaking mode until tapped (same rule as before). */}
            {!!(question.hints?.hint1?.text ||
              question.hints?.hint2?.text ||
              question.hints?.hint1?.images?.length ||
              question.hints?.hint2?.images?.length) &&
              !isPreviewStatic && !shouldRevealCorrectness && (
                <TouchableOpacity
                  onPress={() => toggleHint(qid)}
                  style={S.hintButton}
                >
                  <Text style={S.hintText}>
                    <MaterialCommunityIcons
                      name="lightbulb-outline"
                      size={18}
                      color="#FFF"
                    />{" "}
                    {showHintNow ? t("Hide Hint") : t("Show Hint")}
                  </Text>
                </TouchableOpacity>
              )}

            {showHintNow && !isPreviewStatic && !shouldRevealCorrectness && (
              <View style={{ marginTop: 8 }}>
                {/* hint1 */}
                {Array.isArray(question?.hints?.hint1?.segments) ? (
                  <SegmentsRendererRN
                    segments={question.hints.hint1.segments}
                    isDarkMode={isDarkMode}
                    baseFontSize={16}
                  />
                ) : (
                  <>
                    {!!question?.hints?.hint1?.text && (
                      <Text style={S.hintText}>
                        {question.hints.hint1.text}
                      </Text>
                    )}
                    {question?.hints?.hint1?.images?.map(
                      (image, imgIndex) => (
                        <Image
                          key={`h1-${imgIndex}`}
                          source={{ uri: image.base64 }}
                          style={{
                            width:
                              image.width && image.width < 150
                                ? image.width
                                : image.orientation ===
                                  "horizontal"
                                ? "100%"
                                : "50%",
                            height:
                              image.height && image.height < 150
                                ? image.height
                                : image.orientation ===
                                  "vertical"
                                ? 300
                                : 250,
                            resizeMode: "contain",
                            marginVertical: 5,
                          }}
                        />
                      )
                    )}
                  </>
                )}

                {/* hint2 */}
                {Array.isArray(question?.hints?.hint2?.segments) ? (
                  <SegmentsRendererRN
                    segments={question.hints.hint2.segments}
                    isDarkMode={isDarkMode}
                    baseFontSize={16}
                  />
                ) : (
                  <>
                    {!!question?.hints?.hint2?.text && (
                      <Text style={S.hintText}>
                        {question.hints.hint2.text}
                      </Text>
                    )}
                    {question?.hints?.hint2?.images?.map(
                      (image, imgIndex) => (
                        <Image
                          key={`h2-${imgIndex}`}
                          source={{ uri: image.base64 }}
                          style={{
                            width:
                              image.width && image.width < 150
                                ? image.width
                                : image.orientation ===
                                  "horizontal"
                                ? "100%"
                                : "50%",
                            height:
                              image.height && image.height < 150
                                ? image.height
                                : image.orientation ===
                                  "vertical"
                                ? 300
                                : 250,
                            resizeMode: "contain",
                            marginVertical: 5,
                          }}
                        />
                      )
                    )}
                  </>
                )}
              </View>
            )}

            {/* ✅ Solution button & content
               We never show solutions in previewStatic or during live testTaking.
               We *can* show in quizPractice / pastTestView like you had. */}
            {!isPreviewStatic && !isTestMode && !shouldRevealCorrectness && (
              <TouchableOpacity
                onPress={() => toggleSolution(qid)}
                style={S.solutionButton}
              >
                <Text style={S.solutionButtonText}>
                  <MaterialCommunityIcons
                    name="lightbulb-on-outline"
                    size={18}
                    color="#FFF"
                  />{" "}
                  {showSolNow ? t("Hide Solution") : t("Show Solution")}
                </Text>
              </TouchableOpacity>
            )}

            {!isPreviewStatic && !isTestMode && !shouldRevealCorrectness && showSolNow && (
              <View style={{ marginTop: 8 }}>
                {Array.isArray(question?.solution?.segments) ? (
                  <SegmentsRendererRN
                    segments={question.solution.segments}
                    isDarkMode={isDarkMode}
                    baseFontSize={16}
                  />
                ) : (
                  <>
                    <Text style={S.solutionText}>
                      {question.solution?.text ||
                        t("No solution available")}
                    </Text>
                    {question.solution?.images?.length > 0 &&
                      question.solution.images.map(
                        (image, imgIndex) => (
                          <Image
                            key={imgIndex}
                            source={{ uri: image.base64 }}
                            style={{
                              width:
                                image.width &&
                                image.width < 150
                                  ? image.width
                                  : image.orientation ===
                                    "horizontal"
                                  ? "100%"
                                  : "50%",
                              height:
                                image.height &&
                                image.height < 150
                                  ? image.height
                                  : image.orientation ===
                                    "vertical"
                                  ? 300
                                  : 250,
                              resizeMode: "contain",
                              marginTop: 5,
                            }}
                          />
                        )
                      )}
                  </>
                )}
              </View>
            )}

            {/* BDD 08/30/2026 - full Ask AI help is available only in completed-test review */}
            {(isReviewMode || isPastTestView) && typeof onAskAI === "function" && (
              <View
                dataSet={{ html2canvasIgnore: "true" }}
                style={{
                  marginTop: 16,
                  alignItems: "flex-start",
                }}
              >
                <TouchableOpacity
                  accessibilityRole="button"
                  onPress={() =>
                    handleAskAIForReviewedQuestion(question, index)
                  }
                  style={{
                    paddingVertical: 10,
                    paddingHorizontal: 16,
                    borderRadius: 8,
                    backgroundColor: "#2563eb",
                  }}
                >
                  <Text
                    style={{
                      color: "#ffffff",
                      fontWeight: "600",
                    }}
                  >
                    Ask AI about this question
                  </Text>
                </TouchableOpacity>
              </View>
            )}
          </View>
        );
      })}

      {/* ── Previous / Next navigation (paginated modes only, hidden when parent renders nav) ── */}
      {isPaginated && quiz.length > 1 && !onNavStateChange && (
        <View style={S.paginationNav}>
          <TouchableOpacity
            onPress={() =>
              setCurrentQuestionIndex((prev) => Math.max(0, prev - 1))
            }
            disabled={currentQuestionIndex === 0}
            style={[
              S.paginationButton,
              currentQuestionIndex === 0 && S.paginationButtonDisabled,
            ]}
          >
            <MaterialCommunityIcons
              name="chevron-left"
              size={20}
              color={
                currentQuestionIndex === 0
                  ? isDarkMode
                    ? "#475569"
                    : "#9ca3af"
                  : isDarkMode
                  ? "#818cf8"
                  : "#4f46e5"
              }
            />
            <Text
              style={[
                S.paginationButtonText,
                currentQuestionIndex === 0 && S.paginationButtonTextDisabled,
              ]}
            >
              {t("Previous")}
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            onPress={() =>
              setCurrentQuestionIndex((prev) =>
                Math.min(quiz.length - 1, prev + 1)
              )
            }
            disabled={currentQuestionIndex === quiz.length - 1}
            style={[
              S.paginationButton,
              currentQuestionIndex === quiz.length - 1 &&
                S.paginationButtonDisabled,
            ]}
          >
            <Text
              style={[
                S.paginationButtonText,
                currentQuestionIndex === quiz.length - 1 &&
                  S.paginationButtonTextDisabled,
              ]}
            >
              {t("Next")}
            </Text>
            <MaterialCommunityIcons
              name="chevron-right"
              size={20}
              color={
                currentQuestionIndex === quiz.length - 1
                  ? isDarkMode
                    ? "#475569"
                    : "#9ca3af"
                  : isDarkMode
                  ? "#818cf8"
                  : "#4f46e5"
              }
            />
          </TouchableOpacity>
        </View>
      )}
    </>
  );
}
